import React, { useState } from 'react';
import UploadFileThumbnail from './UploadFileThumbnail';

// Number of visible items in the list at once
const VISIBLE = 3;

/**
 * Thumbnail list with windowed navigation.
 * Shows max 5 items — up/down buttons shift the visible window.
 * Only rendered when more than one file is selected.
 * Locked during upload — no remove, no select.
 * @param {UploadFileItem[]} files
 * @param {number} selectedIndex
 * @param {boolean} locked
 */
export default function UploadFileList({ files, selectedIndex, locked }) {
    const [offset, setOffset] = useState(0);

    // Single file — list not shown, dialog shows dropzone + form only
    if (files.length <= 1) return null;

    const canUp = offset > 0;
    const canDown = offset + VISIBLE < files.length;
    const visible = files.slice(offset, offset + VISIBLE);

    /**
     * Shifts visible window up by one — disabled at top.
     */
    const handleUp = () => {
        if (canUp) setOffset((o) => o - 1);
    };

    /**
     * Shifts visible window down by one — disabled at bottom.
     */
    const handleDown = () => {
        if (canDown) setOffset((o) => o + 1);
    };

    return (
        <div id="upload-file-list" className="upload-file-list">

            {/* Nav header — file count left, up/down + range right */}
            <div className="upload-file-list-nav">
                <span className="upload-file-list-label">{files.length} files selected</span>
                <div className="upload-file-list-nav-buttons">
                    <button
                        className="upload-file-nav-btn"
                        onClick={handleUp}
                        disabled={!canUp}
                        aria-label="scroll up"
                    >
                        ▲
                    </button>
                    <span className="upload-file-nav-range">
                        {offset + 1}–{Math.min(offset + VISIBLE, files.length)}
                    </span>
                    <button
                        className="upload-file-nav-btn"
                        onClick={handleDown}
                        disabled={!canDown}
                        aria-label="scroll down"
                    >
                        ▼
                    </button>
                </div>
            </div>

            {/* Visible window — always max VISIBLE items */}
            <div className="upload-file-list-items">
                {visible.map((item) => {
                    // Real index in full files array — needed for handler dispatch
                    const realIndex = files.indexOf(item);
                    return (
                        <UploadFileThumbnail
                            key={item.getKey()}
                            item={item}
                            index={realIndex}
                            selected={realIndex === selectedIndex}
                            locked={locked}
                        />
                    );
                })}
            </div>
        </div>
    );
}
